/**
 *
 * Styles for OfferCard
 *
 */

import styled from "styled-components";
import { breakpoints } from "root/libs/ui-styleguide/src";
import {
  Headline,
  IconButton,
  IconButtonTooltip,
} from "root/libs/ui-components/src";
import { DhlText } from "@dhl-official/react-library";

export const StyledOfferCard = styled.div`
  position: relative;
  background-color: var(--dui-color-white-500);
  box-shadow: 0 var(--gaq-size-space-3px) var(--dui-size-space-4x) 0
    var(--dui-color-gray-300);
  border-radius: var(--dui-size-radius-md);
  padding-bottom: var(--gaq-size-space-70px);

  &:hover {
    box-shadow: 0 var(--gaq-size-space-3px) var(--dui-size-space-4x) 0
      var(--dui-color-black-opacity-45);
  }

  &:focus,
  &:focus-within {
    box-shadow: 0 var(--dui-size-space-1x) var(--gaq-size-space-7px)
      var(--dui-size-space-1) var(--dui-color-gray-500);
    outline: 2px solid transparent;
  }

  &.highlighted {
    border: var(--dui-size-width-border-sm) solid var(--dui-color-yellow-500);
    order: -1;
  }

  @media screen and (min-width: ${breakpoints.tablet}) {
    grid-area: ${(props) => `card-${props.offerIndex}`};

    &.highlighted {
      z-index: 1;
      scale: ${(props) => (props.expandedCardIndex === null ? `1.1` : false)};
      overflow: ${(props) => (props.iAmExpanded ? "hidden" : false)};
      order: revert;
    }
  }
`;

export const StyledContainerGridArea = styled.div`
  position: relative;

  @media screen and (min-width: ${breakpoints.tablet}) {
    display: ${(props) => (props.iAmExpanded ? "grid" : false)};
    grid-template-columns: ${(props) =>
      props.iAmExpanded ? "repeat(2, minmax(0, 1fr))" : false};
  }

  & > .vector-background {
    position: absolute;
    top: -22px;

    @media screen and (min-width: ${breakpoints.tablet}) {
      top: ${(props) => (props.iAmExpanded ? "-38px" : false)};
      left: ${(props) => (props.iAmExpanded ? "-70px" : false)};
      z-index: -1;
    }
  }
`;

export const StyledEstimatedDeliveryText = styled(DhlText)`
  @media screen and (min-width: ${breakpoints.tablet}) {
    grid-column: ${(props) => (props.iAmExpanded ? "span 2" : false)};
  }
  & > *:is(p, span) {
    margin-top: 20px;
    margin-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? "var(--dui-size-space-20x)"
        : "var(--dui-size-space-10x)"};
    margin-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? "var(--dui-size-space-20x)"
        : "var(--dui-size-space-10x)"};

    @media screen and (min-width: ${breakpoints.tablet}) {
      margin-top: ${(props) =>
        props.iAmExpanded && !props.isHighlighted ? 0 : false};
    }
  }
`;

export const StyledEstimatedDeliveryInfo = styled(DhlText)`
  @media screen and (min-width: ${breakpoints.tablet}) {
    grid-column: ${(props) => (props.iAmExpanded ? "span 2" : false)};
    order: ${(props) => (props.iAmExpanded ? 3 : false)};
  }
  & > *:is(p, span) {
    margin-top: 20px;
    margin-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? "var(--dui-size-space-20x)"
        : "var(--dui-size-space-10x)"};
    margin-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? "var(--dui-size-space-20x)"
        : "var(--dui-size-space-10x)"};
    margin-bottom: 1.4rem;
  }
`;

export const StyledTextAndTooltip = styled.div`
  display: flex;
  margin-bottom: var(--dui-size-space-10x);
  align-items: center;
  position: relative;
  flex-wrap: wrap;
  flex-basis: 100%;
`;

export const StyledIconButtonTooltip = styled(IconButtonTooltip)`
  position: absolute;
  right: 0;
  top: 0;

  @media screen and (min-width: ${breakpoints.tablet}) {
    right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null ? "20px" : false};
  }
`;

export const StyledPriceContainer = styled.div`
  display: flex;
  position: relative;
  padding-top: var(--dui-size-space-10x);
  padding-bottom: var(--dui-size-space-20x);
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? `var(--dui-size-space-10x)`
        : ""};
    margin-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? `var(--dui-size-space-10x)`
        : ""};
    order: ${(props) => (props.iAmExpanded ? 2 : false)};
    justify-content: ${(props) => (props.iAmExpanded ? "center" : false)};
    padding: ${(props) => (props.iAmExpanded ? 0 : false)};
  }
`;

export const StyledContainer = styled.div`
  padding: var(--dui-size-space-5x) var(--dui-size-space-10x)
    var(--dui-size-space-10x) var(--dui-size-space-10x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? `var(--dui-size-space-10x)`
        : ``};
    margin-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? `var(--dui-size-space-10x)`
        : ``};
    order: ${(props) => (props.iAmExpanded ? 1 : false)};
  }
`;

export const StyledCTAWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  padding: var(--dui-size-space-5x) var(--dui-size-space-10x)
    var(--dui-size-space-10x) var(--dui-size-space-10x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    padding-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? `var(--dui-size-space-20x)`
        : ``};
    padding-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? `var(--dui-size-space-20x)`
        : ``};
    order: ${(props) => (props.iAmExpanded ? 3 : false)};
    grid-column: ${(props) => (props.iAmExpanded ? "span 2" : false)};
  }
`;

export const StyledLine = styled.hr`
  border: 0;
  height: 1px;
  background: ${(props) =>
    props.isHighlighted
      ? `var(--dui-color-yellow-500)`
      : `var(--dui-color-gray-300)`};

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null
        ? `var(--dui-size-space-20x)`
        : `var(--dui-size-space-10x)`};
    margin-left: ${(props) =>
      props.isLast && props.expandedCardIndex === null
        ? `var(--dui-size-space-20x)`
        : `var(--dui-size-space-10x)`};
    order: ${(props) => (props.iAmExpanded ? 3 : false)};
    grid-column: ${(props) => (props.iAmExpanded ? "span 2" : false)};
  }
`;

export const StyledExpandCollapseOffer = styled(IconButton)`
  border: none;
  position: absolute;
  right: 20px;
  bottom: 20px;

  @media screen and (min-width: ${breakpoints.tablet}) {
    right: ${(props) =>
      props.isFirst && props.expandedCardIndex === null ? "40px" : false};
  }

  &:focus,
  &:active {
    border: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px #d40511;
  }
`;

export const StyledAdditionalOfferDetails = styled.div`
  padding: 2rem;
  @media screen and (min-width: ${breakpoints.tablet}) {
    display: flex;
    flex-wrap: wrap;
  }
`;

export const StyledAdditionalInformationHeadline = styled(Headline)`
  margin-top: var(--dui-size-space-10x) !important;
`;
